'use client';

import { useMemo } from 'react';
import { JobBlock, ViewMode, WeatherData } from './CalendarInteractive';
import JobBlockCard from './JobBlockCard';

interface CalendarGridProps {
  viewMode: ViewMode;
  currentDate: Date;
  jobs: JobBlock[];
  selectedJobs: string[];
  onJobClick: (job: JobBlock) => void;
  onJobSelect: (jobId: string) => void;
  weatherMap: Record<string, WeatherData>;
  conflictMap: Record<string, Set<string>>;
}

const HOURS = Array.from({ length: 13 }, (_, i) => i + 6); // 6am - 6pm
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function getWeekDates(date: Date): Date[] {
  const d = new Date(date);
  const day = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(start);
    dd.setDate(start.getDate() + i);
    return dd;
  });
}

function getMonthDates(date: Date): Date[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dates: Date[] = [];
  for (let i = 0; i < firstDay; i++) {
    const d = new Date(year, month, -firstDay + i + 1);
    dates.push(d);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    dates.push(new Date(year, month, i));
  }
  while (dates.length % 7 !== 0) {
    const last = dates[dates.length - 1];
    const d = new Date(last);
    d.setDate(last.getDate() + 1);
    dates.push(d);
  }
  return dates;
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function WeatherStrip({ weather }: { weather: WeatherData }) {
  const riskColors = {
    low: 'bg-green-50 border-green-200 text-green-700',
    medium: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    high: 'bg-red-50 border-red-200 text-red-700',
  };
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[10px] font-caption ${riskColors[weather.risk]}`}>
      <span className="text-sm">{weather.icon}</span>
      <span>{weather.condition}</span>
      <span className="font-semibold">{weather.temp}</span>
      {weather.risk === 'high' && <span className="font-bold text-red-600">⚠ Risk</span>}
    </div>
  );
}

export default function CalendarGrid({ viewMode, currentDate, jobs, selectedJobs, onJobClick, onJobSelect, weatherMap, conflictMap }: CalendarGridProps) {
  const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);
  const monthDates = useMemo(() => getMonthDates(currentDate), [currentDate]);
  const todayStr = useMemo(() => toDateStr(new Date()), []);

  const jobsByDate = useMemo(() => {
    const map: Record<string, JobBlock[]> = {};
    jobs.forEach(j => {
      if (!map[j.date]) map[j.date] = [];
      map[j.date].push(j);
    });
    return map;
  }, [jobs]);

  if (viewMode === 'month') {
    return (
      <div className="flex-1 overflow-auto">
        <div className="grid grid-cols-7 border-b border-border bg-muted/50 sticky top-0 z-10">
          {DAYS_SHORT.map(d => (
            <div key={d} className="py-2 text-center font-caption text-xs text-muted-foreground font-medium uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {monthDates.map((date, idx) => {
            const dateStr = toDateStr(date);
            const dayJobs = jobsByDate[dateStr] || [];
            const isCurrentMonth = date.getMonth() === currentDate.getMonth();
            const isToday = dateStr === todayStr;
            const hasConflict = !!conflictMap[dateStr];
            const weather = weatherMap[dateStr];
            return (
              <div
                key={idx}
                className={`min-h-[120px] border-b border-r border-border p-1.5 ${!isCurrentMonth ? 'bg-muted/30' : 'bg-card'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full font-body text-sm font-medium ${
                    isToday ? 'bg-primary text-white' : isCurrentMonth ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {date.getDate()}
                  </div>
                  <div className="flex items-center gap-1">
                    {weather && <span className="text-sm" title={`${weather.condition} ${weather.temp}`}>{weather.icon}</span>}
                    {hasConflict && (
                      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Crew double-booked" />
                    )}
                  </div>
                </div>
                <div className="space-y-0.5">
                  {dayJobs.slice(0, 3).map(job => (
                    <JobBlockCard
                      key={job.id}
                      job={job}
                      compact
                      selected={selectedJobs.includes(job.id)}
                      onClick={() => onJobClick(job)}
                      onSelect={() => onJobSelect(job.id)}
                      isConflict={hasConflict && !!job.crew && conflictMap[dateStr]?.has(job.crew)}
                    />
                  ))}
                  {dayJobs.length > 3 && (
                    <p className="font-caption text-xs text-muted-foreground pl-1">+{dayJobs.length - 3} more</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (viewMode === 'week') {
    return (
      <div className="flex-1 overflow-auto">
        {/* Day headers */}
        <div className="grid sticky top-0 z-10 bg-card border-b border-border" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
          <div className="border-r border-border" />
          {weekDates.map((date, i) => {
            const dateStr = toDateStr(date);
            const isToday = dateStr === todayStr;
            const hasConflict = !!conflictMap[dateStr];
            const weather = weatherMap[dateStr];
            return (
              <div key={i} className={`py-2 px-1 text-center border-r border-border last:border-r-0 ${isToday ? 'bg-primary/5' : ''}`}>
                <p className="font-caption text-xs text-muted-foreground uppercase">{DAYS_SHORT[date.getDay()]}</p>
                <div className="flex items-center justify-center gap-1 mt-0.5">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full font-body text-sm font-semibold ${
                    isToday ? 'bg-primary text-white' : 'text-foreground'
                  }`}>
                    {date.getDate()}
                  </div>
                  {hasConflict && (
                    <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Crew conflict" />
                  )}
                </div>
                {weather && (
                  <div className="mt-1 flex justify-center">
                    <span className="text-xs" title={`${weather.condition} ${weather.temp} — ${weather.risk} risk`}>
                      {weather.icon} <span className="text-muted-foreground">{weather.temp}</span>
                      {weather.risk === 'high' && <span className="text-red-500 ml-0.5">⚠</span>}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <div className="relative" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
          {HOURS.map(hour => (
            <div key={hour} className="grid border-b border-border" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
              <div className="py-3 pr-2 text-right border-r border-border">
                <span className="font-data text-xs text-muted-foreground">
                  {hour === 12 ? '12 PM' : hour < 12 ? `${hour} AM` : `${hour - 12} PM`}
                </span>
              </div>
              {weekDates.map((date, di) => {
                const dateStr = toDateStr(date);
                const isToday = dateStr === todayStr;
                const hourJobs = (jobsByDate[dateStr] || []).filter(j => j.startHour === hour);
                return (
                  <div
                    key={di}
                    className={`min-h-[56px] border-r border-border last:border-r-0 p-0.5 relative ${isToday ? 'bg-primary/3' : ''}`}
                  >
                    {hourJobs.map(job => (
                      <JobBlockCard
                        key={job.id}
                        job={job}
                        compact={false}
                        selected={selectedJobs.includes(job.id)}
                        onClick={() => onJobClick(job)}
                        onSelect={() => onJobSelect(job.id)}
                        isConflict={!!conflictMap[dateStr]?.has(job.crew)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Day view
  const dateStr = toDateStr(currentDate);
  const dayJobs = jobsByDate[dateStr] || [];
  const dayWeather = weatherMap[dateStr];
  const hasConflict = !!conflictMap[dateStr];

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-3xl mx-auto px-4 py-4">
        {/* Day-view weather strip */}
        {dayWeather && (
          <div className="mb-3">
            <WeatherStrip weather={dayWeather} />
          </div>
        )}
        {hasConflict && (
          <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs font-caption">
            <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
            Crew conflict detected — one or more crews are double-booked today
          </div>
        )}
        <div className="space-y-1">
          {HOURS.map(hour => {
            const hourJobs = dayJobs.filter(j => j.startHour === hour);
            return (
              <div key={hour} className="flex gap-3 min-h-[64px]">
                <div className="w-16 flex-shrink-0 pt-1 text-right">
                  <span className="font-data text-xs text-muted-foreground">
                    {hour === 12 ? '12:00 PM' : hour < 12 ? `${hour}:00 AM` : `${hour - 12}:00 PM`}
                  </span>
                </div>
                <div className="flex-1 border-t border-border pt-1 space-y-1">
                  {hourJobs.map(job => (
                    <JobBlockCard
                      key={job.id}
                      job={job}
                      compact={false}
                      selected={selectedJobs.includes(job.id)}
                      onClick={() => onJobClick(job)}
                      onSelect={() => onJobSelect(job.id)}
                      fullWidth
                      isConflict={!!conflictMap[dateStr]?.has(job.crew)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}